/**
 * Pestaña "Importar YouTube" - Flujo inline sin modales.
 * Seleccionar canal → Vista previa en página → Seleccionar → Importar.
 */
import { useState, useCallback, useEffect } from 'react';
import { Youtube, Loader2, CheckSquare, Square, Download, Play, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AcademyCreatorItem } from '../types';
import type { YouTubeVideoPreview } from '../hooks/useAcademyAdmin';
import { useYouTubePreview, useYouTubeSync } from '../hooks/useAcademyAdmin';
import { AcademyCreateCreatorForm } from './AcademyCreateCreatorForm';

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

interface AcademyYouTubeImportTabProps {
    creators: AcademyCreatorItem[];
    creatorsLoading: boolean;
    preselectedCreatorId: string | null;
    onCreatorCreated: () => void;
    onImportSuccess: (result: { inserted: number; skipped: number }) => void;
    onImportError?: (message: string) => void;
}

export function AcademyYouTubeImportTab({
    creators,
    creatorsLoading,
    preselectedCreatorId,
    onCreatorCreated,
    onImportSuccess,
    onImportError,
}: AcademyYouTubeImportTabProps) {
    const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(preselectedCreatorId);
    const [videos, setVideos] = useState<YouTubeVideoPreview[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [showAddCreator, setShowAddCreator] = useState(false);

    const previewMutation = useYouTubePreview();
    const syncMutation = useYouTubeSync();

    const creator = creators.find(c => c.id === selectedCreatorId);
    const canLoadPreview = !!selectedCreatorId && !showAddCreator;
    const creatorLabel =
        creator?.channel_display_name ||
        creator?.user_display_name ||
        creator?.user_email ||
        creator?.youtube_channel_url ||
        (selectedCreatorId ? 'Creador seleccionado' : '');

    const loadPreview = useCallback(() => {
        if (!selectedCreatorId) return;
        previewMutation.mutate(
            { creatorId: selectedCreatorId, maxVideos: 200 },
            {
                onSuccess: (data) => {
                    setVideos(data.videos);
                    setSelected(new Set());
                },
                onError: (err) => onImportError?.(err instanceof Error ? err.message : 'Error al cargar vista previa'),
            }
        );
    }, [selectedCreatorId, previewMutation, onImportError]);

    useEffect(() => {
        if (preselectedCreatorId) {
            setSelectedCreatorId(preselectedCreatorId);
            setShowAddCreator(false);
        }
    }, [preselectedCreatorId]);

    useEffect(() => {
        if (selectedCreatorId && !showAddCreator) {
            setVideos([]);
            setSelected(new Set());
        }
    }, [selectedCreatorId, showAddCreator]);

    const toggleSelect = (videoId: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(videoId)) next.delete(videoId);
            else next.add(videoId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === videos.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(videos.map(v => v.videoId)));
        }
    };

    const importSelected = () => {
        if (!selectedCreatorId) return;
        const ids = selected.size > 0 ? Array.from(selected) : undefined;
        syncMutation.mutate(
            { creatorId: selectedCreatorId, videoIds: ids, maxVideos: Math.max(videos.length, 200) },
            {
                onSuccess: r => {
                    onImportSuccess({ inserted: r.inserted, skipped: r.skipped });
                    setVideos(prev => prev.filter(v => !ids?.includes(v.videoId)));
                    setSelected(new Set());
                },
                onError: (err) => onImportError?.(err instanceof Error ? err.message : 'Error al importar vídeos'),
            }
        );
    };

    const importAll = () => {
        if (!selectedCreatorId) return;
        syncMutation.mutate(
            { creatorId: selectedCreatorId, maxVideos: Math.max(videos.length, 200) },
            {
                onSuccess: r => {
                    onImportSuccess({ inserted: r.inserted, skipped: r.skipped });
                    setVideos([]);
                    setSelected(new Set());
                },
                onError: (err) => onImportError?.(err instanceof Error ? err.message : 'Error al importar vídeos'),
            }
        );
    };

    const approvedCreators = creators.filter(c => c.status === 'approved' || c.status === 'pending');

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Youtube size={18} />
                    Importar vídeos desde YouTube
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                    Selecciona un canal o añade uno nuevo. Carga la vista previa y elige qué vídeos importar a la Academia.
                </p>

                {approvedCreators.length === 0 && !showAddCreator && (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4 mb-4">
                        <p className="text-sm text-foreground">
                            No hay canales disponibles. Pulsa &quot;Añadir canal nuevo&quot; para registrar un canal de YouTube.
                        </p>
                    </div>
                )}

                {/* Paso 1: Selector de canal */}
                <div className="space-y-2 mb-4">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Canal de YouTube
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1 min-w-0">
                            <select
                                value={selectedCreatorId ?? ''}
                                onChange={e => {
                                    setShowAddCreator(false);
                                    setSelectedCreatorId(e.target.value || null);
                                }}
                                disabled={creatorsLoading}
                                className={cn(
                                    "w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground",
                                    "focus:outline-none focus:ring-1 focus:ring-primary/50 appearance-none pr-8"
                                )}
                            >
                                <option value="">Seleccionar creador...</option>
                                {selectedCreatorId && !approvedCreators.some(c => c.id === selectedCreatorId) && (
                                    <option value={selectedCreatorId}>
                                        {creatorLabel}
                                    </option>
                                )}
                                {approvedCreators.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.channel_display_name || c.user_display_name || c.user_email || c.youtube_channel_url}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown
                                size={16}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowAddCreator(!showAddCreator);
                                if (showAddCreator) setSelectedCreatorId(null);
                            }}
                            className={cn(
                                "px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap",
                                showAddCreator
                                    ? "bg-muted text-muted-foreground hover:bg-muted/80"
                                    : "bg-primary/10 text-primary hover:bg-primary/20"
                            )}
                        >
                            {showAddCreator ? 'Cancelar' : '+ Añadir canal nuevo'}
                        </button>
                    </div>
                </div>

                {showAddCreator && (
                    <div className="mb-4 pt-4 border-t border-border/50">
                        <AcademyCreateCreatorForm
                            onCreated={(creatorId) => {
                                setShowAddCreator(false);
                                if (creatorId) setSelectedCreatorId(creatorId);
                                onCreatorCreated();
                            }}
                            onCancel={() => setShowAddCreator(false)}
                        />
                    </div>
                )}

                {/* Paso 2: Cargar vista previa */}
                {canLoadPreview && (
                    <div className="flex flex-col gap-2 pt-2">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={loadPreview}
                                disabled={previewMutation.isPending}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                {previewMutation.isPending ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Play size={14} />
                                )}
                                {previewMutation.isPending ? 'Cargando...' : 'Cargar vista previa'}
                            </button>
                            <span className="text-xs text-muted-foreground">
                                {creatorLabel}
                            </span>
                        </div>
                        {previewMutation.isError && (
                            <p className="text-xs text-red-400">
                                {previewMutation.error instanceof Error
                                    ? previewMutation.error.message
                                    : 'Error al cargar la vista previa'}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Paso 3: Grid de vídeos (inline, no modal) */}
            {videos.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <button
                            onClick={toggleSelectAll}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                            {selected.size === videos.length ? <CheckSquare size={14} /> : <Square size={14} />}
                            {selected.size === videos.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                        </button>
                        <span className="text-xs text-muted-foreground">
                            {selected.size} de {videos.length} seleccionados
                        </span>
                        <div className="flex-1" />
                        <button
                            onClick={importSelected}
                            disabled={syncMutation.isPending || selected.size === 0}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                            {syncMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            Importar seleccionados ({selected.size})
                        </button>
                        <button
                            onClick={importAll}
                            disabled={syncMutation.isPending}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-foreground hover:bg-muted disabled:opacity-50"
                        >
                            Importar todos
                        </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {videos.map(v => (
                            <div
                                key={v.videoId}
                                className={cn(
                                    'rounded-lg border bg-card overflow-hidden transition-all group cursor-pointer',
                                    selected.has(v.videoId)
                                        ? 'ring-2 ring-primary border-primary'
                                        : 'border-border hover:border-primary/50'
                                )}
                                onClick={() => toggleSelect(v.videoId)}
                            >
                                <div className="relative aspect-video bg-muted/50">
                                    <img
                                        src={v.thumbUrl || `https://img.youtube.com/vi/${v.videoId}/mqdefault.jpg`}
                                        alt={v.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <a
                                        href={`https://www.youtube.com/watch?v=${v.videoId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <Play size={32} className="text-white drop-shadow" fill="white" />
                                    </a>
                                    <div
                                        className="absolute top-2 left-2"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <button
                                            onClick={() => toggleSelect(v.videoId)}
                                            className="p-1 rounded bg-black/50 text-white"
                                        >
                                            {selected.has(v.videoId) ? <CheckSquare size={16} /> : <Square size={16} />}
                                        </button>
                                    </div>
                                    {v.durationSeconds != null && (
                                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white">
                                            {formatDuration(v.durationSeconds)}
                                        </span>
                                    )}
                                </div>
                                <p className="p-2 text-xs text-foreground line-clamp-2">{v.title}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {canLoadPreview && !previewMutation.isPending && videos.length === 0 && (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-12 text-center">
                    <Youtube size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">
                        Haz clic en &quot;Cargar vista previa&quot; para ver los vídeos del canal.
                    </p>
                </div>
            )}
        </div>
    );
}
